"use client";

import { useState, useEffect } from "react";
import { Icons } from "./ui/icons";
import { Link, useLocation } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";

import { useUser } from "../hooks/use-user";
import {
  ACCOUNT_NAV_ITEMS,
  MAIN_NAV_ITEMS,
  canSeeNavItem,
  getUserInitials,
  isNavItemActive,
} from "../lib/navigation";

interface NavItemProps {
  href: string;
  icon: React.ReactNode;
  label: string;
  isCollapsed: boolean;
  isSelected: boolean;
}

interface CollapsibleSideNavigationProps {
  defaultCollapsed?: boolean;
}

const AccountMenuContent: React.FC<{ role?: string | null; onLogout: () => void }> = ({
  role,
  onLogout,
}) => (
  <DropdownMenuContent
    align="end"
    className="w-[200px] p-2 bg-white shadow-lg rounded-lg border border-gray-100"
    sideOffset={4}
  >
    {ACCOUNT_NAV_ITEMS.filter((item) => canSeeNavItem(item, role)).map((item) => (
      <DropdownMenuItem key={item.href} asChild>
        <Link
          to={item.href}
          className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md"
        >
          <item.icon className="mr-2 h-4 w-4 text-gray-500" />
          {item.label}
        </Link>
      </DropdownMenuItem>
    ))}
    <DropdownMenuItem
      onClick={onLogout}
      className="flex items-center w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md"
    >
      <Icons.Logout className="mr-2 h-4 w-4 text-red-600" />
      Logout
    </DropdownMenuItem>
  </DropdownMenuContent>
);

export default function CollapsibleSideNavigation({
  defaultCollapsed = false,
}: CollapsibleSideNavigationProps) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const { user, logout } = useUser();
  const location = useLocation();

  // Collapse sidebar by default when on create quotation or create invoice
  useEffect(() => {
    if (
      location.pathname === "/create-quotation" ||
      location.pathname === "/create-invoice"
    ) {
      setIsCollapsed(true);
    }
  }, [location.pathname]);

  const initials = getUserInitials(user?.fullName);

  return (
    <nav
      className={`h-screen bg-gray-50 text-gray-700 transition-all duration-300 ease-in-out flex flex-col ${isCollapsed ? "w-16 pl-4 pr-2 py-4" : "w-64 p-4"
        }`}
    >
      <TooltipProvider>
        <div
          className={`mb-8 flex ${isCollapsed ? "justify-center" : "justify-between"
            } items-center`}
        >
          {!isCollapsed && (
            <h2 className="text-sm font-semibold text-gray-900">Wapo Scaffolding</h2>
          )}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className={`p-1 rounded-full hover:bg-gray-200 text-gray-600 ${isCollapsed ? "-ml-1" : "ml-auto"
              }`}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? (
              <Icons.ChevronRight className="w-5 h-5 text-gray-500" />
            ) : (
              <Icons.ChevronLeft className="w-5 h-5 text-gray-500" />
            )}
          </button>
        </div>
        <ul className="space-y-1 flex-grow overflow-y-auto">
          {MAIN_NAV_ITEMS.filter((item) => canSeeNavItem(item, user?.role)).map((item) => (
            <NavItem
              key={item.href}
              href={item.href}
              icon={<item.icon className="w-5 h-5 text-primary-600" />}
              label={item.label}
              isCollapsed={isCollapsed}
              isSelected={isNavItemActive(item, location.pathname)}
            />
          ))}
        </ul>
        <div className="mt-auto pt-4 border-t border-gray-100">
          {!isCollapsed && user && (
            <div>
              <DropdownMenu>
                <DropdownMenuTrigger className="w-full outline-none">
                  <div className="px-3 py-2 flex items-center group cursor-pointer hover:bg-gray-100 rounded-lg transition-colors duration-150">
                    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-black text-white flex items-center justify-center text-sm font-medium">
                      {initials}
                    </div>
                    <div className="ml-3 min-w-0 flex-1 text-left">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {user.fullName}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {user.email}
                      </p>
                    </div>
                  </div>
                </DropdownMenuTrigger>
                <AccountMenuContent role={user.role} onLogout={logout} />
              </DropdownMenu>
            </div>
          )}
          {isCollapsed && user && (
            <div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <DropdownMenu>
                      <DropdownMenuTrigger className="outline-none">
                        <div className="w-8 h-8 rounded-lg bg-black text-white flex items-center justify-center text-sm font-medium hover:ring-2 hover:ring-gray-200 transition-all cursor-pointer">
                          {initials}
                        </div>
                      </DropdownMenuTrigger>
                      <AccountMenuContent role={user.role} onLogout={logout} />
                    </DropdownMenu>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right" variant="simple">
                  {user.fullName || user.email}
                </TooltipContent>
              </Tooltip>
            </div>
          )}
        </div>
      </TooltipProvider>
    </nav>
  );
}

function NavItem({
  href,
  icon,
  label,
  isCollapsed,
  isSelected,
}: NavItemProps) {
  const sharedClassNames = `flex items-center h-9 transition-colors duration-150 text-sm ${isSelected ? "bg-gray-100 text-gray-900" : "hover:bg-gray-200 text-gray-700"
    } ${isCollapsed ? "w-9 justify-center rounded-lg pr-0" : "px-3 rounded-lg"}`;

  const iconElement = (
    <span
      className={`flex items-center justify-center ${isCollapsed ? "w-9 h-9" : "mr-3"
        } ${isSelected ? "text-primary-600" : "text-gray-600"}`}
    >
      {icon}
    </span>
  );

  const labelElement = !isCollapsed && (
    <span className="flex-grow">{label}</span>
  );

  const wrappedIcon = isCollapsed ? (
    <Tooltip>
      <TooltipTrigger asChild>{iconElement}</TooltipTrigger>
      <TooltipContent side="right" variant="simple">
        {label}
      </TooltipContent>
    </Tooltip>
  ) : (
    iconElement
  );

  return (
    <li>
      <Link to={href} className={sharedClassNames}>
        {wrappedIcon}
        {labelElement}
      </Link>
    </li>
  );
}
